import React, { useState, useEffect, useRef } from "react";
import {
  collection, addDoc, onSnapshot, getDocs,
  query, orderBy, limit, serverTimestamp,
} from "firebase/firestore";
import { db } from "../firebase";
import { connect } from "react-redux";
import genericProfilePic from "../genericprofilepic.png";

function WhosOnline({ first_name, last_name, profile_pic_url }) {
  const userId = localStorage.getItem("user_id");
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const chatEndRef = useRef(null);

  useEffect(() => {
    getDocs(collection(db, "users")).then(snap => {
      setAllUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });

    const unsubPresence = onSnapshot(collection(db, "presence"), snap => {
      const fiveMinAgo = new Date(Date.now() - 5 * 60 * 1000);
      setOnlineUsers(
        snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          // last_seen is refreshed every minute while the app is open, so a stale
          // timestamp means the tab was closed without a clean logout.
          .filter(p => p.online !== false && p.last_seen?.toDate?.() > fiveMinAgo)
      );
    });

    const unsubChat = onSnapshot(
      query(collection(db, "groupChat"), orderBy("created_at", "asc"), limit(100)),
      snap => {
        setMessages(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      }
    );

    return () => {
      unsubPresence();
      unsubChat();
    };
  }, []);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = async () => {
    const text = inputText.trim();
    if (!text) return;
    await addDoc(collection(db, "groupChat"), {
      user_id: userId,
      first_name: first_name || "",
      last_name: last_name || "",
      profile_pic_url: profile_pic_url || null,
      message: text,
      created_at: serverTimestamp(),
    });
    setInputText("");
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleMention = (user) => {
    setInputText(prev => `${prev}@${user.first_name} ${user.last_name} `);
  };

  const formatTime = (ts) => {
    if (!ts) return "";
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleTimeString("en-us", { hour: "numeric", minute: "2-digit" });
  };

  const boxStyle = {
    background: "rgba(255,255,255,0.88)",
    borderRadius: "16px",
    width: "85%",
    maxWidth: "640px",
    margin: "0 auto 20px",
    boxShadow: "0 2px 10px rgba(13,59,140,0.1)",
  };

  return (
    <div style={{ minHeight: "100vh", background: "#F0F7FF", paddingBottom: "40px" }}>
      <br /><br />
      <h2>Who's Online</h2>

      {/* Online users */}
      <div style={{ ...boxStyle, padding: "16px 20px" }}>
        <p style={{ fontFamily: "Hebrew", fontSize: "1.5rem", color: "#0D3B8C", fontWeight: "bold", marginBottom: "12px", marginTop: 0 }}>
          {onlineUsers.length} online now
        </p>
        <div style={{ display: "flex", flexWrap: "wrap", gap: "10px" }}>
          {onlineUsers.length === 0 && (
            <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#999", fontStyle: "italic", margin: 0 }}>
              No one else is online right now.
            </p>
          )}
          {onlineUsers.map(presence => {
            const user = allUsers.find(u => u.id === presence.user_id);
            if (!user) return null;
            return (
              <div
                key={presence.id}
                onClick={() => handleMention(user)}
                title={`Tap to @mention ${user.first_name}`}
                style={{ display: "flex", alignItems: "center", gap: "8px", background: "#EFF6FF", borderRadius: "20px", padding: "6px 14px 6px 8px", cursor: "pointer", border: "2px solid #BFDBFE", transition: "background 0.15s" }}
              >
                <div style={{ width: "9px", height: "9px", borderRadius: "50%", background: "#22C55E", flexShrink: 0 }} />
                <img
                  src={user.profile_pic_url || genericProfilePic}
                  alt={user.first_name}
                  style={{ width: "30px", height: "30px", borderRadius: "50%", objectFit: "cover" }}
                />
                <span style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#0D3B8C" }}>
                  {user.first_name} {user.last_name}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      {/* Group chat */}
      <div style={{ ...boxStyle, display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div style={{ padding: "14px 20px", borderBottom: "2px solid #DBEAFE" }}>
          <p style={{ fontFamily: "Hebrew", fontSize: "1.8rem", fontWeight: "bold", color: "#0D3B8C", margin: 0 }}>
            Group Chat
          </p>
        </div>

        {/* Messages */}
        <div style={{ height: "400px", overflowY: "auto", padding: "16px 20px", display: "flex", flexDirection: "column", gap: "16px" }}>
          {messages.length === 0 && (
            <p style={{ fontFamily: "Hebrew", fontSize: "1.4rem", color: "#bbb", fontStyle: "italic", textAlign: "center", marginTop: "80px" }}>
              No messages yet — say hello! 👋
            </p>
          )}
          {messages.map(msg => {
            const isMe = msg.user_id === userId;
            return (
              <div key={msg.id} style={{ display: "flex", gap: "10px", flexDirection: isMe ? "row-reverse" : "row", alignItems: "flex-start" }}>
                <img
                  src={msg.profile_pic_url || genericProfilePic}
                  alt={msg.first_name}
                  style={{ width: "36px", height: "36px", borderRadius: "50%", objectFit: "cover", flexShrink: 0 }}
                />
                <div style={{ maxWidth: "72%" }}>
                  <p style={{ fontFamily: "Hebrew", fontSize: "1.2rem", color: "#999", margin: "0 0 3px", textAlign: isMe ? "right" : "left" }}>
                    {isMe ? "You" : `${msg.first_name} ${msg.last_name}`}
                    {msg.created_at && (
                      <span style={{ marginLeft: "6px", fontSize: "1.1rem" }}>{formatTime(msg.created_at)}</span>
                    )}
                  </p>
                  <div style={{
                    background: isMe ? "#0D3B8C" : "#F0F7FF",
                    color: isMe ? "white" : "#1A1A2E",
                    borderRadius: isMe ? "16px 4px 16px 16px" : "4px 16px 16px 16px",
                    padding: "10px 14px",
                    fontFamily: "Hebrew",
                    fontSize: "1.5rem",
                    lineHeight: "1.5",
                    border: isMe ? "none" : "1.5px solid #DBEAFE",
                    wordBreak: "break-word",
                  }}>
                    {msg.message}
                  </div>
                </div>
              </div>
            );
          })}
          <div ref={chatEndRef} />
        </div>

        {/* Input */}
        <div style={{ padding: "12px 16px", borderTop: "2px solid #DBEAFE", display: "flex", flexDirection: "column", gap: "8px" }}>
          <textarea
            value={inputText}
            onChange={e => setInputText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Type a message… tap a user above to @mention"
            rows={1}
            style={{ width: "100%", boxSizing: "border-box", padding: "10px 14px", fontSize: "15px", borderRadius: "12px", border: "2px solid #DBEAFE", fontFamily: "Hebrew", resize: "none", minHeight: "44px", maxHeight: "120px", lineHeight: "1.5", outline: "none" }}
          />
          <div style={{ display: "flex", justifyContent: "flex-end" }}>
            <button
              onClick={handleSend}
              className="brownroundbutton"
              style={{ cursor: "pointer", margin: 0, padding: "10px 22px" }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
      <br />
    </div>
  );
}

const mapStateToProps = (state) => ({
  first_name: state.first_name,
  last_name: state.last_name,
  profile_pic_url: state.profile_pic_url,
});

export default connect(mapStateToProps, {})(WhosOnline);
